import { useState, useEffect, useCallback } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, Search, ChevronLeft, ChevronRight, BellRing, RotateCcw, Eye } from 'lucide-react';
import { notificacaoEnvioService, NotificacaoEnvio, NotificacaoEnvioStatus } from '@/services/api';
import { useToast } from '@/hooks/use-toast';

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const STATUS_LABEL: Record<NotificacaoEnvioStatus, string> = {
  pendente: 'Pendente',
  processando: 'Processando',
  enviado: 'Enviado',
  falha: 'Falha',
};

function StatusBadge({ status }: { status: NotificacaoEnvioStatus }) {
  if (status === 'enviado') {
    return <Badge className="bg-green-600 hover:bg-green-600 text-white">{STATUS_LABEL[status]}</Badge>;
  }
  if (status === 'falha') {
    return <Badge variant="destructive">{STATUS_LABEL[status]}</Badge>;
  }
  if (status === 'processando') {
    return <Badge variant="outline">{STATUS_LABEL[status]}</Badge>;
  }
  return <Badge variant="secondary">{STATUS_LABEL[status]}</Badge>;
}

const LIMIT = 50;

export default function NotificacaoEnvios() {
  const { toast } = useToast();
  const [envios, setEnvios] = useState<NotificacaoEnvio[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState('todos');
  const [destinatarioInput, setDestinatarioInput] = useState('');
  const [destinatario, setDestinatario] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [detalhe, setDetalhe] = useState<NotificacaoEnvio | null>(null);
  const [reenvioAlvo, setReenvioAlvo] = useState<NotificacaoEnvio | null>(null);
  const [isReenviando, setIsReenviando] = useState(false);

  const load = useCallback(async (currentPage: number) => {
    setIsLoading(true);
    const res = await notificacaoEnvioService.getAll({
      page: currentPage,
      limit: LIMIT,
      status: status !== 'todos' ? status : undefined,
      destinatario: destinatario || undefined,
      from: from || undefined,
      to: to || undefined,
    });
    if (res.success) {
      setEnvios(res.data);
      setTotal(res.total);
    }
    setIsLoading(false);
  }, [status, destinatario, from, to]);

  useEffect(() => {
    load(page);
  }, [load, page]);

  const handleSearch = () => {
    setDestinatario(destinatarioInput);
    setPage(1);
  };

  const handleClear = () => {
    setDestinatarioInput('');
    setDestinatario('');
    setStatus('todos');
    setFrom('');
    setTo('');
    setPage(1);
  };

  const handleReenviar = async () => {
    if (!reenvioAlvo) return;
    setIsReenviando(true);
    const res = await notificacaoEnvioService.reenviar(reenvioAlvo.id);
    setIsReenviando(false);
    setReenvioAlvo(null);
    if (res.success) {
      toast({ title: 'Reenvio agendado', description: 'O envio voltou para a fila e será processado em instantes.' });
      load(page);
    } else {
      toast({ title: 'Erro ao reenviar', description: res.error, variant: 'destructive' });
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <AppLayout>
      <div className="space-y-4">
        <div className="rounded-lg border bg-card p-4 flex items-center gap-2">
          <BellRing className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-bold">Envios de Notificação</h1>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">Filtros</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3 md:flex-row md:items-end">
              <div className="flex-1">
                <Input
                  placeholder="Buscar por destinatário..."
                  value={destinatarioInput}
                  onChange={(e) => setDestinatarioInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <div className="w-full md:w-44">
                <label className="text-xs text-muted-foreground mb-1 block">Status</label>
                <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="pendente">Pendente</SelectItem>
                    <SelectItem value="processando">Processando</SelectItem>
                    <SelectItem value="enviado">Enviado</SelectItem>
                    <SelectItem value="falha">Falha</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 md:contents">
                <div className="min-w-0 flex-1 md:flex-none md:w-40">
                  <label className="text-xs text-muted-foreground mb-1 block">De</label>
                  <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} className="w-full min-w-0" />
                </div>
                <div className="min-w-0 flex-1 md:flex-none md:w-40">
                  <label className="text-xs text-muted-foreground mb-1 block">Até</label>
                  <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} className="w-full min-w-0" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSearch} size="sm">
                  <Search className="h-4 w-4 mr-2" />
                  Buscar
                </Button>
                <Button onClick={handleClear} variant="outline" size="sm">
                  Limpar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-medium">Envios registrados</CardTitle>
            <Badge variant="secondary">{total} registros</Badge>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : envios.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground text-sm">
                Nenhum envio encontrado
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Módulo / Evento</TableHead>
                      <TableHead className="hidden md:table-cell">Canal</TableHead>
                      <TableHead>Destinatário</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden md:table-cell">Tentativas</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {envios.map((envio) => (
                      <TableRow key={envio.id}>
                        <TableCell className="text-sm whitespace-nowrap">{formatDate(envio.createdAt)}</TableCell>
                        <TableCell>
                          <div className="text-sm font-medium">{envio.modulo}</div>
                          <div className="text-xs text-muted-foreground">{envio.evento}</div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <Badge variant="outline">{envio.canal}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[220px] truncate">
                          {envio.destinatario}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={envio.status} />
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                          {envio.tentativas}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button variant="ghost" size="icon" onClick={() => setDetalhe(envio)} title="Detalhes">
                            <Eye className="h-4 w-4" />
                          </Button>
                          {(envio.status === 'falha' || envio.status === 'enviado') && (
                            <Button variant="ghost" size="icon" onClick={() => setReenvioAlvo(envio)} title="Reenviar">
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <span className="text-sm text-muted-foreground">
                  Página {page} de {totalPages}
                </span>
                <div className="flex gap-1">
                  <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="icon" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!detalhe} onOpenChange={(open) => !open && setDetalhe(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes do envio</DialogTitle>
          </DialogHeader>
          {detalhe && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-xs text-muted-foreground">Status</div>
                  <StatusBadge status={detalhe.status} />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Canal</div>
                  <div>{detalhe.canal}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Módulo / Evento</div>
                  <div>{detalhe.modulo} / {detalhe.evento}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Tentativas</div>
                  <div>{detalhe.tentativas}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs text-muted-foreground">Destinatário</div>
                  <div className="break-all">{detalhe.destinatario}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs text-muted-foreground">Assunto</div>
                  <div>{detalhe.assunto || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Criado em</div>
                  <div>{formatDate(detalhe.createdAt)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Enviado em</div>
                  <div>{formatDate(detalhe.enviado_em)}</div>
                </div>
                {detalhe.provider_message_id && (
                  <div className="col-span-2">
                    <div className="text-xs text-muted-foreground">ID no provedor</div>
                    <div className="break-all font-mono text-xs">{detalhe.provider_message_id}</div>
                  </div>
                )}
              </div>
              {detalhe.erro && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Erro</div>
                  <pre className="bg-destructive/10 text-destructive rounded p-2 text-xs whitespace-pre-wrap break-all max-h-40 overflow-y-auto">{detalhe.erro}</pre>
                </div>
              )}
              <div>
                <div className="text-xs text-muted-foreground mb-1">Payload</div>
                <pre className="bg-muted rounded p-2 text-xs whitespace-pre-wrap break-all max-h-40 overflow-y-auto">{JSON.stringify(detalhe.payload, null, 2)}</pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!reenvioAlvo} onOpenChange={(open) => !open && setReenvioAlvo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reenviar notificação?</AlertDialogTitle>
            <AlertDialogDescription>
              O envio para {reenvioAlvo?.destinatario} voltará para a fila e será processado novamente em instantes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isReenviando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleReenviar} disabled={isReenviando}>
              {isReenviando ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reenviar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
